import type { TFunction } from "i18next";
import { z } from "zod";

export const isGovernmentCompany = (
  companyTypes: ReadonlyArray<{ companyTypeCode: string; companyTypeDesc: string }>,
  selectedCompanyTypes: readonly string[] = [],
): boolean =>
  companyTypes.some(
    ({ companyTypeCode, companyTypeDesc }) =>
      selectedCompanyTypes.includes(companyTypeCode) &&
      companyTypeDesc.trim().toUpperCase() === "GOVERNMENT COMPANY",
  );

const MANDATORY_KEY = "licensing.form-validation.mandatory";
const ALLOWED_TEXT_REGEX = /^[A-Za-z0-9 &().-]+$/;
const ALPHANUMERIC_REGEX = /^[A-Za-z0-9]+$/;
const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;
const UAE_COUNTRY_CODE = "971";

export const normalizePhone = (raw: string): string => {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "";

  let cleaned = trimmed.replace(/[()\s.-]/g, "");
  if (cleaned.startsWith("00")) cleaned = `+${cleaned.slice(2)}`;

  return cleaned;
};

// Fax values are stored as local numbers (no country prefix), digits-only, max 10.
export const normalizeLocalFax = (raw: string): string => {
  const compact = String(raw ?? "")
    .trim()
    .replace(/[()\s.-]/g, "");

  if (!compact) return "";

  let digits = compact.replace(/\D/g, "");

  if (compact.startsWith(`+${UAE_COUNTRY_CODE}`)) {
    digits = digits.slice(UAE_COUNTRY_CODE.length);
  } else if (compact.startsWith(`00${UAE_COUNTRY_CODE}`)) {
    digits = digits.slice(UAE_COUNTRY_CODE.length + 2);
  } else if (digits.startsWith(UAE_COUNTRY_CODE) && digits.length > 10) {
    // Defensive support for legacy values saved as 971XXXXXXXXX without + or 00.
    digits = digits.slice(UAE_COUNTRY_CODE.length);
  }

  return digits.slice(0, 10);
};

export const toDate = (v: unknown): Date | null => {
  const toDateFn = (v as any)?.toDate;
  const d =
    v instanceof Date
      ? v
      : typeof toDateFn === "function"
        ? toDateFn.call(v)
        : typeof v === "string"
          ? new Date(v)
          : null;

  return d && !Number.isNaN(d.getTime()) ? d : null;
};

export const formatIsoDate = (date: Date): string => {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const isNotPastDate = (v: unknown): boolean => {
  const d = toDate(v);
  if (!d) return false;

  const value = new Date(d);
  value.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return value >= today;
};

export const requiredString = (label: string, t: TFunction) =>
  z.string().trim().min(1, t(MANDATORY_KEY, { label }));

export const requiredStringMax = (max: number, label: string, t: TFunction) =>
  requiredString(label, t).max(max, t("licensing.form-validation.max-length", { label, max }));

export const requiredStringAndDigitsMax = (max: number, label: string, t: TFunction) =>
  requiredString(label, t)
    .regex(ALPHANUMERIC_REGEX, t("licensing.form-validation.invalid-characters", { label }))
    .max(max, t("licensing.form-validation.max-length-characters-and-digits", { label, max }));

export const requiredAllowedTextMax = (max: number, label: string, t: TFunction) =>
  requiredString(label, t)
    .regex(ALLOWED_TEXT_REGEX, t("licensing.form-validation.invalid-characters", { label }))
    .max(max, t("licensing.form-validation.max-length", { label, max }));

export const requiredEmail = (label: string, t: TFunction) =>
  requiredString(label, t).email(t("licensing.form-validation.invalid-email", { label }));

export const requiredDigitsString = (maxDigits: number, label: string, t: TFunction) =>
  requiredString(label, t)
    .regex(/^\d+$/, t("licensing.form-validation.invalid-number", { label }))
    .max(maxDigits, t("licensing.form-validation.max-length-digits", { label, maxDigits }));

export const requiredLocalFax = (label: string, t: TFunction) =>
  requiredString(label, t)
    .regex(/^\d+$/, t("licensing.form-validation.invalid-number", { label }))
    .max(10, t("licensing.form-validation.max-length-digits", { label, maxDigits: 10 }))
    .refine((value) => !value.startsWith("00") && !value.startsWith(UAE_COUNTRY_CODE), {
      message: t("licensing.form-validation.invalid-number", { label }),
    });

export const requiredPhone = (label: string, t: TFunction) =>
  z
    .string()
    .trim()
    .min(1, t(MANDATORY_KEY, { label }))
    .transform(normalizePhone)
    .refine((v) => E164_PHONE_REGEX.test(v), {
      message: t("licensing.form-validation.invalid-phone", { label }),
    });

export const requiredDate = (label: string, t: TFunction) =>
  z.union([
    z.date({
      required_error: t(MANDATORY_KEY, { label }),
      invalid_type_error: t(MANDATORY_KEY, { label }),
    }),
    z.string().min(1, t(MANDATORY_KEY, { label })),
  ]);

export const requiredDocumentArray = <T extends z.ZodTypeAny>(
  schema: T,
  label: string,
  t: TFunction,
) => z.array(schema).min(1, t(MANDATORY_KEY, { label }));

export const companyTypesSchema = (
  label: string,
  t: TFunction,
  options: ReadonlyArray<{ companyTypeCode: string; companyTypeDesc: string }>,
) =>
  z
    .array(z.string())
    .min(1, t("form-validation.required", { label }))
    .refine(
      (values) =>
        !options.length ||
        values.every((value) => options.some((option) => option.companyTypeCode === value)),
      { message: t("licensing.form-validation.invalid-option", { label }) },
    );
